/**
 * ApprovalActionsPanel — panel akcji akceptanta na karcie rekordu (F17 T17.1.7).
 *
 * Self-contained: z `{ moduleCode, recordId }` sam wykrywa aktywną instancję
 * (`useActiveInstanceForRecord`) i szuka kroku, na którym zalogowany user może TERAZ
 * zdecydować (`InstanceStep.canDecide`, liczone przez serwer dla pytającego). Gdy takiego
 * kroku nie ma — renderuje `null`.
 *
 * Kto widzi przyciski decyzji (decyzja właściciela 2026-10-06): osoba, która MOŻE TERAZ
 * decydować — jest w puli kroku albo jest przełożonym, do którego eskalowano krok — i jeszcze
 * nie głosowała na tym kroku. Sama obecność w puli (`assignedToUserIds`) nie wystarcza: w trybie
 * „wszyscy" / „większość" osoba, która już zagłosowała, nie ma czego wykonać, a eskalowany
 * przełożony spoza puli może decydować, choć w puli go nie ma.
 *
 * Model WF: decyzja = wybór DROGI narysowanej w definicji procesu (krawędź wychodząca z
 * etapu). Nie ma osobnych akcji Odrzuć / Zwróć / Przekaż — odrzucenie czy zwrot to po prostu
 * inna droga. Panel tylko SKŁADA dwa komponenty:
 *   - `DecisionButtons` — przyciski w banerze („Akceptacja" + do 3 dróg + menu pozostałych),
 *   - `AcceptanceDialog` — okno „Akceptacja" (wybór drogi, komentarz, pola wymagane, akceptant
 *     na następnym etapie, procedury kontrolne), które wysyła decyzję.
 * Okno otwiera się zawsze: z „Akceptacji" (droga domyślna), z przycisku drogi (ta droga) albo z
 * pozycji „Cofnij do poprzedniego etapu" w menu „…" (okno z wybranym cofnięciem; pozycja jest, gdy
 * krok ma `stepBack`, także zablokowane — okno wyjaśni powód). Wszystkie trzy idą przez TĘ SAMĄ
 * bramkę zapisu poniżej, z tą samą blokadą i kręciołkiem (przy cofnięciu — na „…").
 * Gdy wołający poda `beforeDecision` (formularz w edycji — najpierw zapis, potem decyzja, patrz
 * `useSaveBeforeDecision`), przycisk nie otwiera okna sam: oddaje mu funkcję otwarcia (z tą
 * samą drogą), a wołający woła ją po udanym zapisie. Bez propsa okno otwiera się od razu.
 *
 * Blokada na czas zapisu (decyzja właściciela 2026-10-07): bramka niesie flagi wyglądu `saving`
 * (zapis formularza trwa) i `waiting` (okno czeka na wynik zapisu z kliknięcia). Gdy którakolwiek
 * jest ustawiona, WSZYSTKIE przyciski decyzji są zablokowane; kręciołek pokazuje tylko kliknięty
 * (panel pamięta, którą drogę kliknięto — „Akceptacja" albo konkretną; droga z menu „…" daje
 * kręciołek na „…"), i tylko dopóki okno czeka (`waiting`) — zwykłe „Zapisz" blokuje bez kręciołka.
 * Klikniętą drogę panel zapamiętuje wyłącznie wtedy, gdy bramka powie, że ZACZĘŁA czekać na zapis
 * dla tego kliknięcia (wynik wywołania ≠ `false`, patrz `DecisionGate`): kliknięcie, które zapis
 * zatrzymała walidacja, otworzyło okno od razu albo zostało zignorowane (zapis z pierwszego
 * kliknięcia jeszcze trwa), niczego nie zapamiętuje — kręciołek zostaje przy pierwszym.
 * Po udanym zapisie okno otwiera się z drogą z TEGO (pierwszego) kliknięcia; po nieudanym blokada
 * i kręciołek znikają. Bramka bez flag (zwykła funkcja) nic nie blokuje.
 *
 * Fokus klawiatury przy bramce: kliknięty przycisk na czas zapisu staje się `disabled`, a przeglądarka
 * zdejmuje z niego fokus — okno „Akceptacja" zapamiętałoby wtedy `body` i po zamknięciu oddało fokus
 * na początek strony. Dlatego panel zapamiętuje przycisk, który wywołał okno (element z fokusem
 * wewnątrz panelu w chwili kliknięcia; dla drogi z menu „…" — przycisk „…", na który menu przenosi
 * fokus przed otwarciem), a po zamknięciu okna oddaje mu fokus — tylko gdy fokus jest nigdzie
 * (`body`), a przycisk nadal jest w DOM i nie jest zablokowany. Fokus ustawiony gdzie indziej zostaje.
 * Poza bramką (okno otwiera się od razu) panel niczego nie zapamiętuje — fokus oddaje samo okno.
 *
 * Drogi dostępne na etapie liczy SERWER (`InstanceStep.availableRoutes`, z warunkami dróg
 * warunkowych) — panel ich nie odtwarza ze snapshotu. Tak samo `defaultRouteVoteClosesStage`
 * (czy głos za drogą domyślną zamknie etap) — okno potrzebuje go do pola akceptanta.
 *
 * Okno należy do KROKU: gdy zmieni się krok czekający na moją decyzję (odświeżone dane, inny
 * etap tej samej sprawy), okno się zamyka — nie otworzy się samo dla nowego kroku z cudzym
 * komentarzem / wyborem drogi.
 */

import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useActiveInstanceForRecord } from '@/hooks/useActiveInstanceForRecord';
import type { DecisionGate } from '@/hooks/useSaveBeforeDecision';

import { AcceptanceDialog } from './AcceptanceDialog';
import { DecisionButtons, type DecisionTarget } from './DecisionButtons';

export interface ApprovalActionsPanelProps {
  moduleCode: string;
  recordId: string;
  /** Wywołane po pomyślnej decyzji (np. refetch karty rekordu). */
  onActed?: () => void;
  /**
   * Bramka przed otwarciem okna decyzji (np. zapis formularza w edycji). Dostaje funkcję
   * otwarcia okna z drogą klikniętego przycisku; brak propsa = okno otwiera się od razu.
   * Wynik wywołania (`DecisionGate`) mówi, czy bramka czeka na zapis dla tego kliknięcia.
   * Flagi `saving` / `waiting` na bramce blokują przyciski i pokazują kręciołek (patrz nagłówek).
   */
  beforeDecision?: DecisionGate;
  /**
   * Tylko przycisk „Akceptacja” (bez przycisków dróg i menu „…”) — dla elementu formularza „Przycisk
   * akceptacji”; okno „Akceptacja” i tak pozwala wybrać drogę. Domyślnie `false` (pełny pasek).
   */
  primaryOnly?: boolean;
  testIdPrefix?: string;
}

export function ApprovalActionsPanel({
  moduleCode,
  recordId,
  onActed,
  beforeDecision,
  primaryOnly = false,
  testIdPrefix = 'approval-actions',
}: ApprovalActionsPanelProps): ReactElement | null {
  const { instance } = useActiveInstanceForRecord(moduleCode, recordId);

  // `null` = okno zamknięte; `{ kind: 'route', routeKey: null }` = otwarte z „Akceptacji" (droga
  // domyślna); `{ kind: 'stepBack' }` = otwarte z pozycji „Cofnij do poprzedniego etapu" w menu „…".
  const [dialog, setDialog] = useState<DecisionTarget | null>(null);
  // Cel kliknięty przy bramce, która ZACZĘŁA czekać na zapis (`routeKey: null` = „Akceptacja") —
  // z niego wiadomo, który przycisk ma kręciołek. Ustawiany tylko wtedy, gdy bramka potwierdzi
  // oczekiwanie dla tego kliknięcia; zerowany, gdy okno się otworzy albo oczekiwanie się skończy.
  const [clicked, setClicked] = useState<DecisionTarget | null>(null);
  // Przycisk, który wywołał okno przez bramkę — do oddania mu fokusu po zamknięciu okna
  // (patrz nagłówek, „Fokus klawiatury"); `null` = okno otwarte bez bramki, panel nic nie oddaje.
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const waiting = beforeDecision?.waiting === true;
  const blocked = waiting || beforeDecision?.saving === true;
  // Koniec oczekiwania (sukces → okno otwarte, porażka → zapomniane okno) zeruje klikniętą drogę.
  useEffect(() => {
    if (!waiting) setClicked(null);
  }, [waiting]);

  // Krok czekający na MOJĄ decyzję: pierwszy, na którym mogę teraz zdecydować. `canDecide`
  // obejmuje już „krok otwarty w sprawie w toku" (serwer), więc statusu nie sprawdzamy osobno.
  const myStep = instance?.steps.find((s) => s.canDecide) ?? null;
  const stepInstanceId = myStep?.stepInstanceId ?? null;

  useEffect(() => {
    setDialog(null);
  }, [stepInstanceId]);

  // Po zamknięciu okna otwartego przez bramkę: fokus wraca na kliknięty przycisk, jeśli nigdzie nie
  // trafił (przycisk zdjął go, gdy na czas zapisu zrobił się `disabled`, więc okno zapamiętało `body`).
  // Efekt biegnie po sprzątnięciu okna (jego dzieci sprzątają wcześniej), więc `activeElement` jest
  // już ostateczny. Fokus ustawiony gdzie indziej, przycisk usunięty z DOM albo zablokowany → nic.
  useEffect(() => {
    if (dialog !== null) return;
    const target = returnFocusRef.current;
    if (target === null) return;
    returnFocusRef.current = null;
    const active = document.activeElement;
    const focusLost = active === null || active === document.body;
    if (focusLost && target.isConnected && !target.matches(':disabled')) target.focus();
  }, [dialog]);

  // `myStep` pochodzi z `instance.steps`, więc niepuste implikuje niepuste `instance` —
  // guardujemy oba jawnie, żeby uniknąć non-null assertion na `instance`.
  if (!myStep || !instance) return null;

  // Otwarcie okna z kliknięcia (droga, „Akceptacja" albo cofnięcie z menu „…") — ZAWSZE przez
  // bramkę zapisu, jeśli wołający ją podał.
  const requestDialog = (target: DecisionTarget): void => {
    // Element z fokusem w panelu w chwili kliknięcia (przy bramce wróci do niego fokus).
    const active = document.activeElement;
    const trigger =
      beforeDecision && active instanceof HTMLElement && panelRef.current?.contains(active) === true
        ? active
        : null;
    const open = (): void => {
      returnFocusRef.current = trigger;
      setClicked(null);
      setDialog(target);
    };
    if (!beforeDecision) {
      open();
      return;
    }
    // Tylko jawne `false` znaczy „bramka nie czeka na zapis dla tego kliknięcia"; wynik
    // `undefined` (bramka-funkcja bez wyniku) traktujemy jak dotąd — jak „czeka".
    if (beforeDecision(open) !== false) setClicked(target);
  };

  return (
    <>
      <div ref={panelRef} data-testid={`${testIdPrefix}-panel`}>
        <DecisionButtons
          routes={myStep.availableRoutes}
          disabled={blocked}
          primaryOnly={primaryOnly}
          loadingTarget={waiting ? clicked : null}
          stepBackAvailable={myStep.stepBack !== null}
          onOpenStepBack={() => requestDialog({ kind: 'stepBack' })}
          onOpen={(routeKey) => requestDialog({ kind: 'route', routeKey })}
          testIdPrefix={testIdPrefix}
        />
      </div>

      <AcceptanceDialog
        open={dialog !== null}
        initialRouteKey={dialog?.kind === 'route' ? dialog.routeKey : null}
        initialStepBack={dialog?.kind === 'stepBack'}
        onClose={() => setDialog(null)}
        stepInstanceId={myStep.stepInstanceId}
        instanceId={instance.instance.id}
        routes={myStep.availableRoutes}
        snapshot={instance.snapshot}
        defaultRouteVoteClosesStage={myStep.defaultRouteVoteClosesStage}
        stepBack={myStep.stepBack}
        onDecided={() => {
          setDialog(null);
          onActed?.();
        }}
        testIdPrefix={testIdPrefix}
      />
    </>
  );
}
